/* =====================================================================
 * useScrollToHashMessage
 *
 * URL hash (#m-{messageId}) 가 들어오면 채팅 컨테이너 안에서 해당
 * data-message-id 요소를 찾아 부드럽게 스크롤한다.
 *
 * - 파일 저장소 "출처" 바로가기 → /agents/{itemId}#m-{msg} 같은
 *   라우트로 이동했을 때, 채팅 페이지가 mount 직후 fetch 가 끝나기
 *   전까지 잠깐 비어 있을 수 있다. requestAnimationFrame 으로 짧게
 *   재시도해(MAX_TRIES) 메시지가 DOM 에 나타나는 즉시 한 번만 스크롤.
 * - 같은 hash 로 재진입(예: 다른 행에서 같은 버블) 했을 때도 동작하도록
 *   key 계열은 (hash, ready) 로 설정한다.
 *
 * 두 가지 모드를 지원:
 *  1) DOM 기반 (containerRef + scrollIntoView)  — 일반 ChatArea 등
 *  2) Virtuoso 기반 (lookup + scrollToIndex)    — WorkspaceSplitLayout
 * ===================================================================== */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const MAX_TRIES = 60;

function parseMessageIdFromHash(hash: string | undefined | null): string | null {
  if (!hash) return null;
  const m = /^#m-(.+)$/.exec(hash);
  if (!m) return null;
  const raw = m[1];
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * DOM-based variant. Looks up `[data-message-id="..."]` inside the given
 * container ref and scrolls it into view (smooth, center).
 *
 * @param containerRef scroll container holding the message bubbles
 * @param ready        true when messages are loaded into the DOM
 *                     (e.g. messages.length > 0). Hash 처리는 ready === true
 *                     이후에만 시작된다.
 */
export function useScrollToHashMessage(
  containerRef: React.RefObject<HTMLElement | null>,
  ready: boolean,
): void {
  const { hash } = useLocation();

  useEffect(() => {
    if (!ready) return;
    const messageId = parseMessageIdFromHash(hash);
    if (!messageId) return;

    let cancelled = false;
    let tries = 0;

    const selector = `[data-message-id="${(window.CSS && CSS.escape ? CSS.escape(messageId) : messageId)}"]`;

    const tick = () => {
      if (cancelled) return;
      const el = containerRef.current?.querySelector<HTMLElement>(selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      tries += 1;
      if (tries < MAX_TRIES) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
    };
  }, [hash, ready, containerRef]);
}

/**
 * Virtuoso-based variant. Uses the caller-provided lookup function
 * (messageId → index) and a scroll callback. Required because Virtuoso
 * does not render off-screen rows into the DOM, so a plain DOM query
 * never finds the target.
 */
export function useScrollToHashMessageByIndex(
  lookupIndex: (messageId: string) => number | undefined,
  scrollToIndex: (index: number) => void,
  ready: boolean,
): void {
  const { hash } = useLocation();

  useEffect(() => {
    if (!ready) return;
    const messageId = parseMessageIdFromHash(hash);
    if (!messageId) return;

    let cancelled = false;
    let tries = 0;

    const tick = () => {
      if (cancelled) return;
      const idx = lookupIndex(messageId);
      if (typeof idx === 'number' && idx >= 0) {
        scrollToIndex(idx);
        return;
      }
      tries += 1;
      if (tries < MAX_TRIES) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);

    return () => {
      cancelled = true;
    };
  }, [hash, ready, lookupIndex, scrollToIndex]);
}
